import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Flex, Text, Button, Input, Select, Checkbox, Progress, Badge,
  Menu, MenuButton, MenuList, MenuItem, Portal, FormControl, FormLabel,
  SimpleGrid, useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { addStore, addArticle, resolveItems } from "./useNewData";

// Walks the pending queue one item at a time (docs/michael/TASK-new-data-tab.md §7 S16).
// kind: 'store' | 'article'. Exclude / Mark-as-DC are store-only — an article
// code in the pattern tables would hide real stores in every report (§7.0 rule 8).

const fmtDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(date);
};

const supplierCodeOf = (s) => (typeof s === "string" ? s : (s && (s.suppliercode || s.code || s.value)) || "");

const preselectRegion = (regions, province) => {
  const p = String(province || "").trim().toLowerCase();
  if (!p) return "";
  const list = regions || [];
  const exact = list.find((r) => String(r).trim().toLowerCase() === p);
  if (exact) return exact;
  const partial = list.find((r) => {
    const rl = String(r).trim().toLowerCase();
    return rl && (rl.includes(p) || p.includes(rl));
  });
  return partial || "";
};

const FoundField = ({ label, children }) => (
  <Box minW={0}>
    <Text fontSize="xs" color="ink.500" fontWeight="600">{label}</Text>
    <Text fontSize="sm" color="ink.800" wordBreak="break-word">{children == null || children === "" ? "—" : children}</Text>
  </Box>
);

const NewDataStepper = ({ items = [], kind, regions = [], suppliers = [], onResolved, onDone }) => {
  const toast = useToast();
  const [queue, setQueue] = useState(items || []);
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // A new items array (parent refetch) restarts the walk at the first pending item.
  useEffect(() => {
    setQueue(items || []);
    setIdx(0);
  }, [items]);

  const total = queue.length;
  const current = queue[idx];
  const details = (current && current.details) || {};
  const isDescStore = kind === "store" && String((current && current.code) || "").startsWith("DESC:");

  const supplierOptions = useMemo(() => {
    const codes = (suppliers || []).map(supplierCodeOf).filter(Boolean);
    const own = current && current.suppliercode;
    if (own && !codes.includes(own)) codes.push(own);
    return codes;
  }, [suppliers, current]);

  // (Re)seed the form whenever the current item changes.
  useEffect(() => {
    if (!current) return;
    const d = current.details || {};
    if (kind === "store") {
      setForm({
        site_code: String(current.code || "").startsWith("DESC:") ? "" : (current.raw_code || current.code || ""),
        site_description: current.description || "",
        region: preselectRegion(regions, d.province),
      });
    } else {
      setForm({
        description: current.description || "",
        unitcost: d.cost != null && d.cost !== "" ? String(d.cost) : "",
        suppliercode: current.suppliercode || "",
        brand: d.brand || "",
        activeyn: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current && current.id, kind]);

  const advance = () => setIdx((i) => i + 1);

  // Two masters share this queue: 200 {alreadyResolved} means the other one got
  // there first — say so and move on (§7.0 rule 8).
  const handleResolvedResponse = (res, okTitle, okDescription) => {
    if (res && res.alreadyResolved) {
      toast({
        title: `Already handled by ${res.resolved_by_name || res.resolved_by || "another master"}`,
        description: res.status ? `This item is already marked "${res.status}".` : undefined,
        status: "info", duration: 3500, isClosable: true,
      });
    } else {
      toast({ title: okTitle, description: okDescription, status: "success", duration: 2500, isClosable: true });
    }
    if (onResolved) onResolved(current, res);
    advance();
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      if (kind === "store") {
        const res = await addStore({
          id: current.id,
          site_code: String(form.site_code || "").trim(),
          site_description: String(form.site_description || "").trim(),
          region: form.region,
        });
        handleResolvedResponse(
          res,
          res && res.existed ? "Already on file — marked as added" : "Store added",
          `${String(form.site_code || "").trim()} → store_details`
        );
      } else {
        const res = await addArticle({
          id: current.id,
          description: String(form.description || "").trim(),
          unitcost: form.unitcost === "" || form.unitcost == null ? 0 : Number(form.unitcost) || 0,
          brand: String(form.brand || "").trim() || form.suppliercode,
          suppliercode: form.suppliercode,
          activeyn: !!form.activeyn,
        });
        handleResolvedResponse(
          res,
          res && res.existed ? "Already on file — marked as added" : "Article added",
          `${current.code} → articles`
        );
      }
    } catch (error) {
      toast({
        title: "Save failed",
        description: error?.response?.data?.message || "Technical error occurred",
        status: "error", duration: 5000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const resolveCurrent = async (action) => {
    if (!current) return;
    setSaving(true);
    try {
      const res = await resolveItems([current.id], action);
      const labels = { ignore: "Ignored", exclude: "Excluded — never imported again", dc: "Marked as DC" };
      handleResolvedResponse(res, labels[action] || "Resolved");
    } catch (error) {
      toast({
        title: "Action failed",
        description: error?.response?.data?.message || "Technical error occurred",
        status: "error", duration: 5000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Empty queue, or walked past the end: all caught up.
  if (!total || idx >= total) {
    return (
      <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <CheckCircleIcon boxSize="28px" color="signal.up" />
        <Text fontWeight="600">All caught up</Text>
        <Text fontSize="sm" color="ink.500">
          No pending {kind === "store" ? "stores" : "articles"} to work through.
        </Text>
        {onDone && (
          <Button size="sm" variant="ghost" onClick={onDone}>Close</Button>
        )}
      </Flex>
    );
  }

  const saveDisabled = saving || (kind === "store"
    ? !form.region || !String(form.site_code || "").trim()
    : !form.suppliercode || !String(form.description || "").trim());

  const detailEntries = Object.entries(details).filter(([k]) => k !== "is_dc_file" && k !== "province" && k !== "brand" && k !== "cost");

  return (
    <Box>
      {/* Progress — BatchReports "n of N" pattern */}
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="bold" fontSize="sm">Item {idx + 1} of {total}</Text>
        <Badge colorScheme="gray">{total - idx} to go</Badge>
      </Flex>
      <Progress value={(idx / total) * 100} colorScheme="teal" size="sm" borderRadius="md" mb={4} />

      {/* Found in file */}
      <Box bg="ink.50" border="1px solid" borderColor="ink.200" borderRadius="6px" p={3} mb={4}>
        <Flex align="center" gap={2} mb={2}>
          <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="ink.500">
            Found in file
          </Text>
          {details.is_dc_file && <Badge colorScheme="purple">DC purchasing org</Badge>}
        </Flex>
        <SimpleGrid columns={{ base: 2, md: 3 }} spacingX={4} spacingY={2}>
          <FoundField label="Code">{current.code}</FoundField>
          {current.raw_code && current.raw_code !== current.code && (
            <FoundField label="Raw code">{current.raw_code}</FoundField>
          )}
          <FoundField label="Description">{current.description}</FoundField>
          <FoundField label="Retailer">{current.retailer}</FoundField>
          <FoundField label="Supplier">{current.suppliercode}</FoundField>
          <FoundField label="File">{current.source_file}</FoundField>
          <FoundField label="Data type">{current.data_type}</FoundField>
          <FoundField label="Source">{current.source}</FoundField>
          <FoundField label="First seen">{fmtDateTime(current.first_seen_at)}</FoundField>
          <FoundField label="Last seen">{fmtDateTime(current.last_seen_at)}</FoundField>
          <FoundField label="Seen">{current.seen_count ? `${current.seen_count}×` : "1×"}</FoundField>
          {detailEntries.map(([k, v]) => (
            <FoundField key={k} label={k.replace(/_/g, " ")}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</FoundField>
          ))}
        </SimpleGrid>
      </Box>

      {/* Form */}
      {kind === "store" ? (
        <>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Site code</FormLabel>
            <Input
              size="sm"
              value={form.site_code || ""}
              isReadOnly={!isDescStore}
              bg={!isDescStore ? "gray.50" : undefined}
              placeholder={isDescStore ? "No code in the file — type the real site code" : undefined}
              onChange={(e) => setForm({ ...form, site_code: e.target.value })}
            />
            {isDescStore && (
              <Text fontSize="xs" color="ink.500" mt={1}>
                This store arrived without a code (matched by description) — a real site code is required.
              </Text>
            )}
          </FormControl>
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Site description</FormLabel>
            <Input size="sm" value={form.site_description || ""} onChange={(e) => setForm({ ...form, site_description: e.target.value })} />
          </FormControl>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Region</FormLabel>
            <Select size="sm" placeholder="Select region" value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })}>
              {(regions || []).map((r, i) => (
                <option key={i} value={r}>{r}</option>
              ))}
            </Select>
          </FormControl>
        </>
      ) : (
        <>
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Article code</FormLabel>
            <Input size="sm" value={current.code || ""} isReadOnly bg="gray.50" />
          </FormControl>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Description</FormLabel>
            <Input size="sm" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Unit cost</FormLabel>
            <Input size="sm" type="number" placeholder="blank = 0" value={form.unitcost ?? ""} onChange={(e) => setForm({ ...form, unitcost: e.target.value })} />
            <Text fontSize="xs" color="ink.500" mt={1}>Optional — blank is stored as 0.</Text>
          </FormControl>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Supplier</FormLabel>
            <Select size="sm" placeholder="Select supplier" value={form.suppliercode || ""} onChange={(e) => setForm({ ...form, suppliercode: e.target.value })}>
              {supplierOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Brand</FormLabel>
            <Input
              size="sm"
              value={form.brand || ""}
              placeholder={form.suppliercode ? `Defaults to ${form.suppliercode}` : "Defaults to the chosen supplier"}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
            />
          </FormControl>
          <Checkbox size="sm" mb={1} isChecked={!!form.activeyn} onChange={(e) => setForm({ ...form, activeyn: e.target.checked })}>
            Active
          </Checkbox>
        </>
      )}

      {/* Actions */}
      <Flex mt={4} gap={2} align="center">
        <Button size="sm" colorScheme="teal" onClick={save} isDisabled={saveDisabled} isLoading={saving}>
          Save & Next
        </Button>
        <Button size="sm" variant="ghost" onClick={advance} isDisabled={saving}>Skip</Button>
        <Menu isLazy>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<ChevronDownIcon />} isDisabled={saving}>
            More
          </MenuButton>
          {/* Portal'd: the stepper renders inside modals/tabs — an inline MenuList gets clipped */}
          <Portal>
            <MenuList fontSize="sm" zIndex={1500}>
              <MenuItem onClick={() => resolveCurrent("ignore")}>Ignore</MenuItem>
              {kind === "store" && (
                <MenuItem onClick={() => resolveCurrent("exclude")}>Exclude — never import this store</MenuItem>
              )}
              {kind === "store" && (
                <MenuItem onClick={() => resolveCurrent("dc")}>Mark as DC</MenuItem>
              )}
            </MenuList>
          </Portal>
        </Menu>
        <Box flex="1" />
        {onDone && (
          <Button size="sm" variant="ghost" onClick={onDone}>Close</Button>
        )}
      </Flex>
    </Box>
  );
};

export default NewDataStepper;
